import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PagePanel from 'src/components/PagePanel';
import PageHeader from 'src/components/PageHeader';
import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { useResetPageParam } from 'src/hooks/useResetPageParam';
import {
  fetchSupportTickets,
  formatSupportStatusLabel,
  formatTicketUserName,
  getTicketUser,
  type SupportTicket,
} from 'src/api/adminSupport';
import { convertDate } from 'src/components/Table/helpers';
import { SUPPORT_HEADERS } from 'src/utils/constants';

import TicketDetailModal from './TicketDetailModal';
import { StatusText, SupportTab, SupportTabs } from './styled';

type SupportTabId = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed';

const TABS: { id: SupportTabId; label: string; status?: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open', status: 'OPEN' },
  { id: 'in_progress', label: 'In Progress', status: 'IN_PROGRESS' },
  { id: 'resolved', label: 'Resolved', status: 'RESOLVED' },
  { id: 'closed', label: 'Closed', status: 'CLOSED' },
];

const statusTone = (raw?: string): 'positive' | 'negative' | 'muted' | 'warning' => {
  switch (String(raw ?? '').toUpperCase()) {
    case 'RESOLVED':
      return 'positive';
    case 'OPEN':
      return 'warning';
    case 'CLOSED':
      return 'muted';
    default:
      return 'muted';
  }
};

const Support = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<SupportTabId>('all');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SupportTicket | null>(null);

  const dispatch = useDispatch();
  const resetPageParam = useResetPageParam();

  const tabStatus = TABS.find((tab) => tab.id === activeTab)?.status;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSupportTickets({
        page: currentPage,
        limit: 10,
        status: tabStatus,
      });
      setTickets(res.tickets);
      setTotalCount(res.meta?.total_count ?? 0);
      setTotalPages(Math.max(1, res.meta?.page_count || 1));
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load support tickets',
          isSuccess: false,
        }),
      );
      setTickets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, tabStatus, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTabChange = (tab: SupportTabId) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setCurrentPage(1);
    // Pagination reads the page from the URL, so local state alone would be
    // overwritten straight back to the page the previous tab was on.
    resetPageParam();
  };

  const rows = useMemo(
    () =>
      tickets.map((ticket) => {
        const created = ticket.created_at ? convertDate(ticket.created_at) : null;
        return {
          ...ticket,
          subject_display: ticket.subject || '—',
          user_display: formatTicketUserName(getTicketUser(ticket)),
          email_display: ticket.email || '—',
          created_display: created ? `${created.date} ${created.time}` : '—',
        };
      }),
    [tickets],
  );

  return (
    <PagePanel>
      <PageHeader title={`Support (${totalCount})`} hideSearch />

      <SupportTabs>
        {TABS.map((tab) => (
          <SupportTab
            key={tab.id}
            type="button"
            $active={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </SupportTab>
        ))}
      </SupportTabs>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={SUPPORT_HEADERS}
          items={rows}
          columns="1.5fr 1fr 1.3fr 0.9fr 1.1fr"
          fieldsToShow={[
            'subject_display',
            'user_display',
            'email_display',
            'status',
            'created_display',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelected(item as SupportTicket)}
          renderCell={(item, field) => {
            if (field === 'status') {
              return (
                <StatusText $tone={statusTone(item.status)}>
                  {formatSupportStatusLabel(item.status)}
                </StatusText>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selected && (
        <TicketDetailModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdated={load}
        />
      )}
    </PagePanel>
  );
};

export default Support;
